import Paho from "paho-mqtt";
import config from "@/config";
import store from "@/store";

export class IoTClient {
	clientId = null;
	iotWebSocket = null;
	connectionPool = null;
	pahoClient = null;
	subscriptions = []; // max 50
	offlineSubscriptions = [];
	pendingMessages = [];

	constructor(iotWebSocket, connectionPool) {
		this.clientId = `[${connectionPool.clientIndex}] ui - ${iotWebSocket.userName} - ${iotWebSocket.uniqueID}`
		this.iotWebSocket = iotWebSocket;
		this.connectionPool = connectionPool;
	}

	getConnectionUrl() {
		const authParams = {
			"x-amz-customauthorizer-name": "iotUIAuthorizer",
			"x-amzn-token-header": store.getters.getAccessToken().split(" ")[1]
		};

		return `${config.MQTT_HOST}/mqtt?${new URLSearchParams(authParams).toString()}`;
	}

	connect() {
		try {
			this.pahoClient = new Paho.Client(this.getConnectionUrl(), this.clientId);
			this.bindEvents();

			this.pahoClient.connect({
				onSuccess: this.onConnectionSuccess.bind(this),
				onFailure: this.onConnectionFailure.bind(this),
				reconnect: true,
				cleanSession: true, //The client and server state is deleted on successful connect. We need to subscribe again in the previous subscriptions
				keepAliveInterval: 10
			});
		} catch (ex) {
			console.error("Error connecting", ex);
		}
	}

	bindEvents() {
		const onConnected = (reconnect, uri) => {
			console.log(`${this.clientId} connected successfully to ${uri} - (reconnect: ${reconnect})`);
		};

		const onConnectionLost = (responseObject) => {
			try {
				if (responseObject.errorCode !== 0) {
					console.warn(`onConnectionLost: ${responseObject.errorMessage} - ClientID: ${this.clientId}`);
				}
			} catch (ex) {
				console.error(`Error for onConnectionLost: ${ex}`);
			}
		};

		const onMessageArrived = (message) => {
			try {
				const topic = message.topic;
				const payload = JSON.parse(message.payloadString.toString("utf-8"));
				this.iotWebSocket.onMessageArrived(topic, payload);
			} catch (ex) {
				console.error(`Error for onMessageArrived: ${ex}`);
			}
		};

		this.pahoClient.onConnected = onConnected.bind(this);
		this.pahoClient.onConnectionLost = onConnectionLost.bind(this);
		this.pahoClient.onMessageArrived = onMessageArrived.bind(this);
	}

	onConnectionSuccess() {
		console.log(`ClientID: ${this.clientId}: Connection succeeded! `);

		for (let sub of this.subscriptions) {
			// console.log(`Adding offline subscription to: ${sub}`);
			this.pahoClient.subscribe(sub, {qos: 1});
		}

		for (let sub of this.offlineSubscriptions) {
			// console.log(`Adding offline subscription to: ${sub}`);
			this.pahoClient.subscribe(sub, {qos: 1});
		}
		this.offlineSubscriptions = [];

		for (let {topic, data} of this.pendingMessages) {
			// console.log(`Publish reconnect message to: ${topic}`);
			this.pahoClient.send(topic, data, 1);
		}
		this.pendingMessages = [];
	}

	onConnectionFailure(error) {
		try {
			console.warn("onConnectionFailure:", error);
			this.connect();
		} catch (ex) {
			console.error(`Error for onFailure: ${ex}`);
		}
	}

	disconnect() {
		if (this.pahoClient.isConnected()) {
			this.pahoClient.disconnect();
		}
	}

	publish(topic, data) {
		if (this.pahoClient.isConnected()) {
			this.pahoClient.send(topic, data, 1);
			// console.log(`Publish message to: ${topic}`);
		}
		else {
			this.pendingMessages.push({topic, data});
			// console.log(`Caching publish message (we are not connected to aws yet) to: ${topic}`);
		}
	}

	subscribe(topic) {
		if (this.pahoClient.isConnected()) {
			// console.log(`Subscribe to topic: ${topic}`);
			this.pahoClient.subscribe(topic, {qos: 1});
		}
		else {
			// console.log(`Caching subscription (we are not connected to aws yet) to: ${topic}`);
			this.offlineSubscriptions.push(topic);
		}
		if (this.subscriptions.length >= 50) {
			throw new Error(`Too many subscriptions in IoTClient: ${this.clientId}`);
		}

		this.subscriptions.push(topic);
	}

	unsubscribe(topic) {
		if (this.pahoClient.isConnected()) {
			this.pahoClient.unsubscribe(topic);
		}
		// console.log(`Unsubscribed from: ${topic}`);
		this.removeTopicSubscriptionFromMemory(topic);
	}

	unsubscribeAll() {
		for (let topic of this.getSubscriptions) {
			this.unsubscribe(topic);
		}
	}

	removeTopicSubscriptionFromMemory(topic) {
		const topicIndex = this.subscriptions.indexOf(topic);
		this.subscriptions.splice(topicIndex, 1);
	}

	get getSubscriptionsLength() {
		return this.subscriptions.length;
	}

	get getSubscriptions() {
		return this.subscriptions;
	}

	get getClientId() {
		return this.clientId;
	}
}